import React from 'react';
import { Card, CardContent } from '../components/ui/card';
import { Target, Eye, Award, Shield } from 'lucide-react';

const About = () => {
  const values = [
    {
      icon: Shield,
      title: 'Quality First',
      description: 'We supply only genuine, traceable components from authorized sources'
    },
    {
      icon: Award,
      title: 'Customer Satisfaction',
      description: 'Dedicated to exceeding customer expectations with exceptional service'
    },
    {
      icon: Target,
      title: 'Reliability',
      description: 'Consistent delivery and support for your component sourcing needs'
    }
  ];

  return (
    <div className="about-page">
      <div className="page-header">
        <div className="container mx-auto px-6 py-16">
          <h1 className="page-title">About Us</h1>
          <p className="page-subtitle">
            Your trusted partner in electronic component distribution
          </p>
        </div>
      </div>

      <div className="container mx-auto px-6 py-12">
        <div className="max-w-4xl mx-auto space-y-12">
          {/* Company Overview */}
          <section>
            <h2 className="text-3xl font-bold mb-6">Company Overview</h2>
            <div className="text-lg leading-relaxed space-y-4">
              <p>
                Shree Techno Products is a premier distributor of electronic components based in 
                Bengaluru, Karnataka. We specialize in semiconductors, integrated circuits, and a 
                comprehensive range of active and passive components.
              </p>
              <p>
                Our company serves a diverse clientele including OEMs, EMS companies, manufacturers, 
                R&D organizations, and industrial buyers. With a strong focus on quality and 
                authenticity, we ensure that every component we supply meets the highest industry 
                standards.
              </p>
              <p>
                Through our global sourcing network and partnerships with leading manufacturers, 
                we provide competitive pricing and reliable supply chain solutions for businesses 
                of all sizes.
              </p>
            </div>
          </section>

          {/* Mission & Vision */}
          <div className="grid md:grid-cols-2 gap-8">
            <Card className="mission-vision-card">
              <CardContent className="p-8">
                <div className="flex items-center gap-3 mb-4">
                  <div className="icon-wrapper-mission">
                    <Target className="h-7 w-7" />
                  </div>
                  <h3 className="text-2xl font-bold">Our Mission</h3>
                </div>
                <p className="text-gray-700 leading-relaxed">
                  To deliver genuine electronic components at competitive prices, ensuring our 
                  customers receive the best value and service in the industry. We are committed 
                  to building long-term partnerships based on trust and reliability.
                </p>
              </CardContent>
            </Card>

            <Card className="mission-vision-card">
              <CardContent className="p-8">
                <div className="flex items-center gap-3 mb-4">
                  <div className="icon-wrapper-vision">
                    <Eye className="h-7 w-7" />
                  </div>
                  <h3 className="text-2xl font-bold">Our Vision</h3>
                </div>
                <p className="text-gray-700 leading-relaxed">
                  To become a trusted global semiconductor sourcing partner, recognized for our 
                  commitment to quality, innovation, and customer success. We aim to be the 
                  first choice for businesses seeking reliable component procurement solutions.
                </p>
              </CardContent>
            </Card>
          </div>

          {/* Core Values */}
          <section>
            <h2 className="text-3xl font-bold mb-8 text-center">Our Core Values</h2>
            <div className="grid md:grid-cols-3 gap-6">
              {values.map((value, index) => {
                const IconComponent = value.icon;
                return (
                  <Card key={index} className="value-card">
                    <CardContent className="p-6 text-center">
                      <div className="inline-flex p-4 rounded-full bg-blue-50 mb-4">
                        <IconComponent className="h-8 w-8 text-blue-600" />
                      </div>
                      <h3 className="text-xl font-bold mb-3">{value.title}</h3>
                      <p className="text-gray-600">{value.description}</p>
                    </CardContent>
                  </Card>
                );
              })}
            </div>
          </section>

          {/* What Sets Us Apart */}
          <section className="mt-12">
            <Card className="commitment-card">
              <CardContent className="p-8">
                <h3 className="text-2xl font-bold mb-6">What Sets Us Apart</h3>
                <ul className="space-y-3">
                  <li className="flex items-start gap-3">
                    <div className="commitment-bullet"></div>
                    <span className="text-gray-700">100% genuine components with full traceability</span>
                  </li>
                  <li className="flex items-start gap-3">
                    <div className="commitment-bullet"></div>
                    <span className="text-gray-700">Extensive network of authorized distributors and manufacturers</span>
                  </li>
                  <li className="flex items-start gap-3">
                    <div className="commitment-bullet"></div>
                    <span className="text-gray-700">Competitive pricing for both bulk and small quantity orders</span>
                  </li>
                  <li className="flex items-start gap-3">
                    <div className="commitment-bullet"></div>
                    <span className="text-gray-700">Fast turnaround time for urgent requirements</span>
                  </li>
                  <li className="flex items-start gap-3">
                    <div className="commitment-bullet"></div>
                    <span className="text-gray-700">Expert technical support and component selection assistance</span>
                  </li>
                  <li className="flex items-start gap-3">
                    <div className="commitment-bullet"></div>
                    <span className="text-gray-700">Flexible payment terms and customized solutions</span>
                  </li>
                </ul>
              </CardContent>
            </Card>
          </section>
        </div>
      </div>
    </div>
  );
};

export default About;
